import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import settingsService from "../../services/settingsService";

interface SettingsState {
  preferences: {
    currency: string;
    timezone: string;
    date_format: string;
    language: string;
    email_notifications: boolean;
    performance_alerts: boolean;
    payment_alerts: boolean;
    balance: number;
    [key: string]: any;
  };
  currencyRates: {
    [key: string]: number;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  preferences: {
    currency: "USD",
    timezone: "UTC",
    date_format: "MM/DD/YYYY",
    language: "en",
    email_notifications: true,
    performance_alerts: true,
    payment_alerts: true,
    balance: 0,
  },
  currencyRates: {
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 148.0,
    CAD: 1.36,
    AUD: 1.52,
  },
  isLoading: false,
  error: null,
};

export const fetchPreferences = createAsyncThunk(
  "settings/fetchPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getPreferences();
      return response.preferences;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch preferences"
      );
    }
  }
);

export const updatePreferences = createAsyncThunk(
  "settings/updatePreferences",
  async (preferences: any, { rejectWithValue }) => {
    try {
      const response = await settingsService.updatePreferences(preferences);
      return response.preferences;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update preferences"
      );
    }
  }
);

export const fetchCurrencyRates = createAsyncThunk(
  "settings/fetchCurrencyRates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getCurrencyRates();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch currency rates"
      );
    }
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setCurrency: (state, action: PayloadAction<string>) => {
      state.preferences.currency = action.payload;
    },
    setBalance: (state, action: PayloadAction<number>) => {
      state.preferences.balance = action.payload;
    },
    updateBalance: (state, action: PayloadAction<number>) => {
      state.preferences.balance = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Preferences
      .addCase(fetchPreferences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = {
          ...state.preferences,
          ...action.payload,
        };
      })
      .addCase(fetchPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update Preferences
      .addCase(updatePreferences.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = {
          ...state.preferences,
          ...action.payload,
        };
      })
      .addCase(updatePreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Currency Rates
      .addCase(fetchCurrencyRates.fulfilled, (state, action) => {
        state.currencyRates = action.payload.rates || state.currencyRates;
      });
  },
});

export const { setCurrency, setBalance, updateBalance, clearError } =
  settingsSlice.actions;
export default settingsSlice.reducer;
